import {
  Calendar,
  CheckCircle,
  Globe,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { formatDate } from "../../Components/Common/FormatDate";
import StudentFormService from "../../services/KangarooServices/StudentFormService";
import { useParams } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";

const StudentDetail = () => {
  const [loading, setLoading] = useState(false);
  const [studentDetail, setStudentDetail] = useState(null);
  const { id } = useParams();

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [adminNotesError, setAdminNotesError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);

  const toggleStatus = () => setIsStatusOpen(!isStatusOpen);
  const statusOptions = [
    { value: 9, label: "New" },
    { value: 1, label: "Success" },
    { value: 2, label: "Contacted" },
    { value: 7, label: "Failed" },
    { value: 3, label: "Call Back Requested" },
    { value: 4, label: "Not Interested" },
    { value: 5, label: "Invalid Contact" },
    { value: 6, label: "Call Not Answered" },
    { value: 8, label: "In Touch Through Other Means" },
  ];
  const fetchDetail = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await StudentFormService.getDetail(id);
      setStudentDetail(result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, []);
  const getStatusColors = (status) => {
    switch (status) {
      case "New":
        return "secondary";
      case "Contacted":
        return "info";
      case "Qualified":
        return "success";
      case "Closed":
        return "dark";
      default:
        return "light";
    }
  };

  const timeAgo = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };
  const getStatusColor = (status) => {
    const colors = {
      Success: "success",
      Ongoing: "warning",
      Failed: "danger",
    };
    return colors[status] || "secondary";
  };

  // Status modal handlers
  const handleStatusUpdate = (item) => {
    setSelectedItem(item);
    const currentStatus = statusOptions.find(
      (status) => status.label === item.formStatus
    );
    setSelectedStatus(currentStatus ? currentStatus.value : null);
    toggleStatus();
  };

  const handleStatusChange = (e) => setSelectedStatus(Number(e.target.value));

  const handleUpdateStatus = async () => {
    if (!adminNotes) {
      setAdminNotesError("Admin Notes is required");
      return;
    }
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    if (selectedItem && selectedStatus !== null) {
      try {
        await StudentFormService.updateStatus(selectedItem.id, {
          adminNotes,
          status: selectedStatus,
        });
        await fetchDetail();

        toast.success("Status updated successfully");
        toggleStatus();
        setSelectedItem(null);
        setSelectedStatus(null);
        setAdminNotes("");
        setAdminNotesError("");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to update status"
        );
      }
    }
  };
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Student Details"
          pageTitle="Student-Forms"
          breadcrumbItems={[{ title: "Back to List", link: "/student-forms" }]}
        />
        <Row>
          <Col>
            <Card className="shadow-sm">
              <CardHeader className="bg-white border-bottom">
                <div className="d-flex justify-content-between align-content-center align-items-center">
                  <h2 className="mb-0 d-flex align-items-center">
                    <User size={32} className="me-2" />
                    Student Details
                  </h2>

                  <Button
                    size="sm"
                    color="warning"
                    onClick={() => handleStatusUpdate(studentDetail)}
                  >
                    <i className="ri-pencil-line"></i>
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {/* Personal Information Section */}
                <div className="mb-4 pb-4 border-bottom">
                  <h5 className="mb-3 d-flex align-items-center">
                    <User size={20} className="me-2" />
                    Personal Information
                  </h5>
                  <Row>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Full Name</strong>
                            </td>
                            <td>{studentDetail?.fullName || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Email</strong>
                            </td>
                            <td>{studentDetail?.email || "N/A"}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Phone</strong>
                            </td>
                            <td>{studentDetail?.phone || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Nationality</strong>
                            </td>
                            <td>{studentDetail?.nationality || "N/A"}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </div>

                {/* Academic Information Section */}
                <div className="mb-4 pb-4 border-bottom">
                  <h5 className="mb-3 d-flex align-items-center">
                    <GraduationCap size={20} className="me-2" />
                    Academic Information
                  </h5>
                  <Row>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Current Qualification</strong>
                            </td>
                            <td>
                              {studentDetail?.currentQualification || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Intended Level</strong>
                            </td>
                            <td>{studentDetail?.intendedLevel || "N/A"}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Preferred Course</strong>
                            </td>
                            <td>
                              {studentDetail?.preferredCourse ||
                                "Not specified"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>English Level</strong>
                            </td>
                            <td>
                              {studentDetail?.englishLevel || "Not specified"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </div>

                {/* Study Preferences Section */}
                <div className="mb-4 pb-4 border-bottom">
                  <h5 className="mb-3 d-flex align-items-center">
                    <MapPin size={20} className="me-2" />
                    Study Preferences
                  </h5>
                  <Row>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Preferred Branch</strong>
                            </td>
                            <td>
                              {studentDetail?.preferedBranchName || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Budget Range</strong>
                            </td>
                            <td>
                              {studentDetail?.budgetRange || "Not specified"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Work Experience</strong>
                            </td>
                            <td>
                              {studentDetail?.workExperienceYears
                                ? `${studentDetail.workExperienceYears} years`
                                : "Not specified"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Lead Source</strong>
                            </td>
                            <td>
                              {studentDetail?.leadSource || "Not specified"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </div>

                {/* ID Information Section */}
              </CardBody>
            </Card>
          </Col>

          <Col>
            <Card>
              <CardBody>
                {/* Application Status Section */}
                <div className="mb-4 pb-4 border-bottom">
                  <h5 className="mb-3 d-flex align-items-center">
                    <CheckCircle size={20} className="me-2" />
                    Application Status
                  </h5>
                  <Row>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Status</strong>
                            </td>
                            <td>
                              <Badge
                                color={getStatusColor(studentDetail?.status)}
                                pill
                                className="px-3 py-2"
                              >
                                {studentDetail?.status}
                              </Badge>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Form Origin</strong>
                            </td>
                            <td>{studentDetail?.formOrigin}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Created At</strong>
                            </td>
                            <td>{formatDate(studentDetail?.createdAt)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={12}>
                      <Table borderless size="sm" className="mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted" style={{ width: "50%" }}>
                              <strong>Updated At</strong>
                            </td>
                            <td>{formatDate(studentDetail?.updatedAt)}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">
                              <strong>Consent Given</strong>
                            </td>
                            <td>
                              <Badge
                                color={
                                  studentDetail?.consent ? "success" : "warning"
                                }
                                pill
                              >
                                {studentDetail?.consent ? "Yes" : "No"}
                              </Badge>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
            <Card className="border-0">
              <CardHeader>Status</CardHeader>
              <CardBody>
                {studentDetail?.statusLogs
                  ?.slice()
                  .reverse()
                  .map((item) => {
                    const isAdmin = item.changedBy === "Admin";

                    return (
                      <div
                        key={item.id}
                        className={`chat-row ${isAdmin ? "admin" : "system"}`}
                      >
                        <div className="chat-bubble">
                          <div className="chat-header">
                            <span className="chat-user">{item.changedBy}</span>
                            <span className="chat-time">
                              {new Date(item.createdAt).toLocaleString()}
                            </span>
                          </div>

                          <div className="chat-status">
                            <span>Status:</span>
                            <strong>
                              {item.previousStatus || "—"} → {item.newStatus}
                            </strong>
                          </div>

                          {item.adminNotes && (
                            <div className="chat-note">{item.adminNotes}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={isStatusOpen} toggle={toggleStatus}>
          <ModalHeader toggle={toggleStatus}>Update Status</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label htmlFor="statusSelect" className="form-label">
                Select Status
              </label>
              <select
                id="statusSelect"
                className="form-select"
                value={selectedStatus || selectedItem?.status || ""}
                onChange={handleStatusChange}
              >
                <option value="">-- Select Status --</option>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <FormGroup>
              <Label for="adminNotes">Admin Notes</Label>
              <Input
                id="adminNotes"
                name="adminNotes"
                type="textarea"
                rows={6}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                onBlur={() => {
                  if (!adminNotes)
                    setAdminNotesError("Admin Notes is required");
                  else setAdminNotesError("");
                }}
                invalid={!!adminNotesError}
              />
              {adminNotesError && (
                <div className="text-danger mt-1">{adminNotesError}</div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-success" onClick={handleUpdateStatus}>
              Update
            </button>
            <button className="btn btn-danger" onClick={toggleStatus}>
              Cancel
            </button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default StudentDetail;
